import { Button } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { SEO } from '../../components/common/SEO';
import { MarketingLayout } from './MarketingLayout';

/**
 * /about - who is behind Rongeka Africa. Company facts here must stay limited to
 * what is published elsewhere (name, products, email, WhatsApp number).
 */
export const AboutPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <MarketingLayout current="/about">
      <SEO
        title="About Rongeka Technologies | Makers of Rongeka Africa and Rongeka Retail"
        description="Rongeka Technologies builds Rongeka Africa, a school management system for Zimbabwean schools covering students, fees, results and WhatsApp communication with parents, and Rongeka Retail, point of sale for shops, supermarkets and schools."
        url="https://rongeka.africa/about"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          url: 'https://rongeka.africa/about',
          name: 'About Rongeka Technologies',
          about: { '@id': 'https://rongeka.africa/#organization' },
          mainEntity: {
            '@type': 'Organization',
            '@id': 'https://rongeka.africa/#organization',
            name: 'Rongeka Technologies',
            url: 'https://rongeka.africa/',
            email: 'support@rongeka.africa',
            telephone: '+263789442202',
          },
        }}
      />

      <header className="mkt-hero">
        <div className="landing-section-label">ABOUT US</div>
        <h1 className="mkt-hero-title">About Rongeka Technologies</h1>
        <p className="mkt-hero-lead">
          Rongeka Technologies is the company behind Rongeka Africa, a school management
          system built for Zimbabwean schools, and Rongeka Retail, point of sale for shops,
          supermarkets and schools.
        </p>
      </header>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Rongeka Africa</h2>
          <p className="mkt-section-body">
            Rongeka Africa runs the day-to-day administration of a school in one place:
            student records and enrolment, attendance, fees and invoicing, assessments,
            results and report cards, and messaging to parents. It is built around how
            Zimbabwean schools work - terms and academic years, levels and classes such as
            Form 2 - Blue, day scholars and boarders, and ZIMSEC-style grading - and it
            puts the information parents and teachers need on WhatsApp, so nobody has to
            install an app.
          </p>
          <ul className="mkt-check-list">
            <li><CheckCircleOutlined />Web platform for administrators, bursars, heads and teachers</li>
            <li><CheckCircleOutlined />WhatsApp access for parents, teachers and students</li>
            <li><CheckCircleOutlined />One full term free, then $0.50 per student per month for the core platform</li>
            <li><CheckCircleOutlined />Optional add-ons, such as accounting, billed separately</li>
          </ul>
          <p className="mkt-section-body">
            See the <RouterLink to="/pricing">pricing</RouterLink>, the{' '}
            <RouterLink to="/features/fees">fees</RouterLink> and{' '}
            <RouterLink to="/features/reports">report card</RouterLink> features, or the{' '}
            <RouterLink to="/faq">frequently asked questions</RouterLink>.
          </p>
        </div>
      </section>

      <section className="mkt-section mkt-section-alt">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Rongeka Retail</h2>
          <p className="mkt-section-body">
            Rongeka Retail is point of sale for shops, supermarkets and schools. Tills,
            canteens and storerooms run on one account, in USD and ZWG, on any phone,
            tablet or computer, and every sale lands in one dashboard. It costs USD 12 per
            machine a month. Find out more at{' '}
            <a href="https://rongekaretail.co.zw" target="_blank" rel="noopener noreferrer">rongekaretail.co.zw</a>.
          </p>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Talk to us</h2>
          <p className="mkt-section-body">
            Email <a href="mailto:support@rongeka.africa">support@rongeka.africa</a> or
            message us on WhatsApp at{' '}
            <a href="https://wa.me/263789442202" target="_blank" rel="noopener noreferrer">+263 78 944 2202</a>.
            More options are on the <RouterLink to="/contact">contact page</RouterLink>.
          </p>
          <div className="mkt-hero-actions">
            <Button type="primary" size="large" onClick={() => navigate('/get-started')}>
              Start Your Free Term
            </Button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
};
